import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation, useQueryParams, useLocalNotificationBanner } from 'Shared/util/custom-hooks';
import { fetchErpIntegration, activateErpIntegration } from 'Shared/util/web-api';
import {
  ActivateErpIntegrationInputResource,
  BrandName,
  ErpIntegrationErrorResource,
  ErpIntegrationErrorTypeResource,
  ErpIntegrationProviderIdResource,
  WebApiError
} from 'Shared/util/types';
import useAppContext from 'Contexts/app-context';
import useCustomerContext from 'Contexts/customer-context';
import { toggleImbox } from 'Shared/util/imbox-api';
import Page from 'Shared/components/page/page';
import Description from 'Shared/components/description';
import Button from 'Shared/components/button';
import TextLink from 'Shared/components/text-link';
import styles from './fortnox-setup-page.css';


const getImagePath = (brandName: BrandName) => {
  switch (brandName) {
    case BrandName.EuroFinans:
      return '/euro-finans/integration/invoice-integration-page.png';

    case BrandName.PrioritetFinans:
      return '/prioritet-finans/integration/invoice-integration-page.png';

    default:
      return '';
  }
};

const FortnoxSetupPage = () => {
  const [isCheckingCallbackStatus, setIsCheckingCallbackStatus] = useState(true);
  const [activationAllowed, setActivationAllowed] = useState(false);
  const [activationUrl, setActivationUrl] = useState<string>();
  const [isActivating, setIsActivating] = useState(false);
  const [showInvoiceIntegrationPageButton, setShowInvoiceIntegrationPageButton] = useState(false);
  const { appData, loadingAppData } = useAppContext();
  const { customerData, loadingCustomerData, updateActiveErpIntegration } = useCustomerContext();
  const history = useHistory();
  const queryParams = useQueryParams();
  const {
    NotificationBannerType,
    addLocalNotificationBanner,
    removeAllLocalNotificationBanners,
  } = useLocalNotificationBanner();
  const t = useTranslation(x => x.fortnox.setup);
  const tg = useTranslation(x => x.global);

  const { activeErpIntegration } = customerData || {};

  useEffect(() => {
    const QUERY_PARAM_CODE = 'code';
    const QUERY_PARAM_STATE = 'state';
    const QUERY_PARAM_ERROR = 'error';
    const QUERY_PARAM_ERROR_DESCRIPTION = 'error_description';

    const getFortnoxQueryParams = () => ({
      activationCode: queryParams?.get(QUERY_PARAM_CODE),
      state: queryParams?.get(QUERY_PARAM_STATE),
      error: queryParams?.get(QUERY_PARAM_ERROR),
      errorDescription: queryParams?.get(QUERY_PARAM_ERROR_DESCRIPTION),
    });

    const clearFortnoxQueryParams = () => {
      queryParams.delete(QUERY_PARAM_CODE);
      queryParams.delete(QUERY_PARAM_STATE);
      queryParams.delete(QUERY_PARAM_ERROR);
      queryParams.delete(QUERY_PARAM_ERROR_DESCRIPTION);
      history.replace({ search: queryParams.toString() });
    };

    const callbackParams = getFortnoxQueryParams();
    const isValidFortnoxCallback = (!!callbackParams.activationCode || !!callbackParams.error);

    if (isValidFortnoxCallback) {
      clearFortnoxQueryParams();
      setIsActivating(true);

      if (callbackParams.activationCode) {
        const payload: ActivateErpIntegrationInputResource = {
          activationCode: callbackParams.activationCode,
          providerId: ErpIntegrationProviderIdResource.Fortnox
        };
        /* NOTE: React might show an incorrect error ("Warning: Can't perform a React state update on an unmounted component...") if component
        have been unmounted on "setState". This should be fixed in upcoming release https://github.com/facebook/react/pull/22114 */
        activateErpIntegration(payload)
          .then((result) => {
            addLocalNotificationBanner(t.connection_activated, NotificationBannerType.Success);
            setShowInvoiceIntegrationPageButton(true);
            updateActiveErpIntegration(result);
          })
          .catch((error: WebApiError<ErpIntegrationErrorResource>) => {
            const {
              type: errorType
            } = error?.data || {};

            if (errorType === ErpIntegrationErrorTypeResource.FortnoxMissingPermissions) {
              addLocalNotificationBanner((tnb) => tnb.fortnox_activation_missing_invoicing_license, NotificationBannerType.Error);
            } else {
              addLocalNotificationBanner((tnb) => tnb.a_technical_error_occurred_try_again, NotificationBannerType.Error);
            }
          })
          .finally(() => {
            setIsCheckingCallbackStatus(false);
            setIsActivating(false);
          });
      } else {
        addLocalNotificationBanner((tnb) => {
          switch (callbackParams.error) {
            case 'error_missing_license':
              return tnb.fortnox_activation_missing_integration_license;

            default:
              return tnb.a_technical_error_occurred_try_again;
          }
        }, NotificationBannerType.Error);
        setIsCheckingCallbackStatus(false);
        setIsActivating(false);
      }
    } else {
      setIsCheckingCallbackStatus(false);
    }
  }, []);

  useEffect(() => {
    if (loadingCustomerData || isCheckingCallbackStatus) {
      return;
    } else if (!activeErpIntegration) {
      /* NOTE: React might show an incorrect error ("Warning: Can't perform a React state update on an unmounted component...") if component
      have been unmounted on "setState". This should be fixed in upcoming release https://github.com/facebook/react/pull/22114 */
      fetchErpIntegration(ErpIntegrationProviderIdResource.Fortnox)
        .then((result) => {
          setActivationAllowed(!!result.oAuth.url);
          setActivationUrl(result.oAuth.url);
        })
        .catch(() => addLocalNotificationBanner(tg.thereWasTechnicalError, NotificationBannerType.Error, false));
    } else {
      switch (activeErpIntegration.providerId) {
        case ErpIntegrationProviderIdResource.Acconomy:
          addLocalNotificationBanner(t.format(t.another_active_erp_integration_warning, { activeProviderName: activeErpIntegration.providerName }), NotificationBannerType.Attention, false);
          break;

        case ErpIntegrationProviderIdResource.Fortnox:
          setActivationUrl(activeErpIntegration.oAuth.url);
          setActivationAllowed(activeErpIntegration.oAuth.reactivationRequired);
          break;

        case ErpIntegrationProviderIdResource.Undefined:
        default:
          addLocalNotificationBanner(tg.thereWasTechnicalError, NotificationBannerType.Error, false);
          break;
      }
    }
  }, [loadingCustomerData, isCheckingCallbackStatus, activeErpIntegration]);

  const handleActivateErpIntegration = () => {
    if (activationUrl) {
      removeAllLocalNotificationBanners();
      window.location.href = activationUrl;
    }
  };

  const handleRedirectToInvoiceIntegrationPage = () => history.push('/invoice-integration');

  return (
    <Page
      title={{
        text: t.header,
        reducedMarginBottom: true,
      }}
      narrowWidth
      loading={loadingAppData || loadingCustomerData}
    >
      <Description text={t.description} />
      <div className={styles.getStartedContainer}>
        <div>
          <h4 className={styles.getStartedHeader}>{t.get_started_header}</h4>
          <p className={styles.getStartedDisclaimer}>{t.get_started_disclaimer}</p>
          <Description text={t.get_started_steps} />
          <Description text={t.get_started_ending} />
        </div>
        <img
          src={getImagePath(appData?.brandName)}
          alt="invoice-integration-page"
          className={styles.invoiceIntegrationPageImage}
        />
      </div>
      <Description
        text={
          <>
            {t.contact_us}{` `}<TextLink text={t.contact_us_chat} link={toggleImbox} />{` `}{t.contact_us_phone}
          </>
        }
        className={styles.contactUsContainer}
      />
      {showInvoiceIntegrationPageButton ?
        <Button
          buttonType="action-primary"
          text={t.show_invoices}
          onClick={handleRedirectToInvoiceIntegrationPage}
          className={styles.actionButton}
        />
        :
        <Button
          buttonType="action-primary"
          text={t.link_account}
          onClick={handleActivateErpIntegration}
          isLoading={isActivating}
          attributes={{ disabled: (!activationAllowed && !isActivating) }}
          className={styles.actionButton}
        />
      }
    </Page>
  );
};

export default FortnoxSetupPage;
